import type { Project } from '../types/tauri';
import type { ProjectGroup } from '../services/backend/IBackend';
import type { ProjectSortMode, ProjectSortDir } from '../stores/uiStore';
import { naturalSortDir } from '../stores/uiStore';
import { useTerminalStore } from '../stores/terminalStore';
import { getProjectGroupId } from './projectSettings';

const normPath = (p: string) => p.replace(/\\/g, '/').toLowerCase();

/** dataTransfer type used when dragging a project row onto a team ("group")
 *  drop target. The payload is the project id. */
export const PROJECT_DND_MIME = 'application/x-ateliercode-project';

/** Sentinel group id for the "Ungrouped" catch-all bucket (collapse state,
 *  drag-and-drop target). Not a real group id. */
export const UNGROUPED_ID = '__ungrouped__';

/** Project paths that currently have at least one open (active) terminal. */
export function openTerminalPaths(): Set<string> {
  const open = new Set<string>();
  for (const s of useTerminalStore.getState().sessions.values()) {
    if (s.status === 'active') open.add(normPath(s.projectPath));
  }
  return open;
}

/**
 * "Active" = the project has ≥1 open terminal session. This is the durable
 * signal the sidebar groups on (NOT the transient activity-pulse). Pass a
 * pre-computed `openPaths` set when partitioning many projects at once.
 */
export function isProjectActive(project: Project, openPaths: Set<string> = openTerminalPaths()): boolean {
  return openPaths.has(normPath(project.root_path));
}

/** Split a (already-sorted) list into [active, inactive], preserving order. */
export function partitionByActive(projects: Project[]): { active: Project[]; inactive: Project[] } {
  const openPaths = openTerminalPaths();
  const active: Project[] = [];
  const inactive: Project[] = [];
  for (const p of projects) (isProjectActive(p, openPaths) ? active : inactive).push(p);
  return { active, inactive };
}

/**
 * One team ("group") bucket in the grouped project view: the group definition
 * (or null for the "Ungrouped" catch-all) plus its projects split Active /
 * Inactive. Projects whose stored group id no longer matches a known group fall
 * into the `null` bucket.
 */
export interface TeamBucket {
  group: ProjectGroup | null;
  active: Project[];
  inactive: Project[];
}

/**
 * Group an (already-sorted) project list by team, each bucket split into
 * Active/Inactive. `groups` must already be in display order. `isActive` is
 * injected so callers pick the activeness source — the local terminal store on
 * the desktop (`isProjectActive`), or the host's session list on a remote
 * client — keeping this helper transport-agnostic. Order: every defined group
 * (even empty ones, so they stay visible as drag-and-drop targets), then the
 * Ungrouped bucket last.
 */
export function groupByTeam(
  projects: Project[],
  groups: ProjectGroup[],
  isActive: (p: Project) => boolean,
): TeamBucket[] {
  const known = new Set(groups.map((g) => g.id));
  const buckets = new Map<string | null, TeamBucket>();
  const ensure = (group: ProjectGroup | null) => {
    const key = group?.id ?? null;
    let b = buckets.get(key);
    if (!b) { b = { group, active: [], inactive: [] }; buckets.set(key, b); }
    return b;
  };
  // Pre-create every defined group so empty teams still render as drop targets.
  for (const g of groups) ensure(g);

  for (const p of projects) {
    const gid = getProjectGroupId(p.settings);
    const group = gid && known.has(gid) ? groups.find((g) => g.id === gid)! : null;
    const b = ensure(group);
    (isActive(p) ? b.active : b.inactive).push(p);
  }

  const ordered: TeamBucket[] = groups.map((g) => buckets.get(g.id)!);
  const ungrouped = buckets.get(null);
  if (ungrouped) ordered.push(ungrouped);
  return ordered;
}

/** Ascending comparator for a given mode. Direction is applied by the caller. */
function ascCompare(a: Project, b: Project, mode: ProjectSortMode): number {
  switch (mode) {
    case 'last_accessed':
      return (a.last_activity || a.created_at) - (b.last_activity || b.created_at);
    case 'date_added':
      return a.created_at - b.created_at;
    case 'alphabetical':
      return a.name.localeCompare(b.name);
    default:
      return 0;
  }
}

/**
 * Sort projects by `mode` and `dir`. When `dir` is omitted, each mode uses its
 * natural default (newest-first for time-based, A→Z for alphabetical) so
 * callers that don't track direction still get a sensible order.
 */
export function sortProjects(
  projects: Project[],
  mode: ProjectSortMode,
  dir?: ProjectSortDir,
): Project[] {
  const effectiveDir = dir ?? naturalSortDir(mode);
  const sign = effectiveDir === 'asc' ? 1 : -1;
  return [...projects].sort((a, b) => sign * ascCompare(a, b, mode));
}
